import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch, buildQueryString } from '../lib/api';
import { formatBRL, formatDatetime, formatMonth } from '../lib/format';
import type { FinanceiroData, FinanceiroMonth, MetaAccount } from '../types';

export default function Financeiro() {
  const [searchParams, setSearchParams] = useSearchParams();
  const account = searchParams.get('account') ?? 'all';

  const [data, setData] = useState<FinanceiroData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [yearFilter, setYearFilter] = useState<string>('all');

  useEffect(() => {
    setIsLoading(true);
    const qs = buildQueryString({ account: account !== 'all' ? account : undefined });
    apiFetch<FinanceiroData>(`/api/metrics/financeiro${qs}`)
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [account]);

  function setAccount(a: string) {
    setSearchParams(sp => { sp.set('account', a); return sp; });
  }

  const accounts: MetaAccount[] = data?.meta.accounts ?? [];
  const allMonths: FinanceiroMonth[] = data?.meta.months ?? [];

  const years = Array.from(new Set(allMonths.map(m => m.monthStart.slice(0, 4)))).sort();

  const filteredMonths = yearFilter === 'all'
    ? allMonths
    : allMonths.filter(m => m.monthStart.startsWith(yearFilter));

  const totals = filteredMonths.reduce(
    (acc, m) => ({
      spend: acc.spend + m.spend,
      tax: acc.tax + m.tax,
      total: acc.total + m.total,
    }),
    { spend: 0, tax: 0, total: 0 }
  );

  const maxSpend = Math.max(...filteredMonths.map(m => m.spend), 1);

  return (
    <div style={{ padding: '28px', maxWidth: '1440px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: 'var(--text-primary)' }}>Financeiro</h1>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-tertiary)' }}>
            Gasto mensal + impostos (ISS/PIS 12,15% a partir de jan/2026)
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Account selector */}
          <div style={{ display: 'flex', gap: '4px' }}>
            {[{ account_id: 'all', name: 'Todas as contas' }, ...accounts].map(acc => (
              <button
                key={acc.account_id}
                onClick={() => setAccount(acc.account_id)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '8px',
                  border: '1px solid',
                  borderColor: account === acc.account_id ? 'var(--accent-border)' : 'var(--border-subtle)',
                  background: account === acc.account_id ? 'var(--accent-muted)' : 'transparent',
                  color: account === acc.account_id ? 'var(--accent)' : 'var(--text-tertiary)',
                  fontSize: '12px',
                  fontFamily: 'var(--font-mono)',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {acc.name}
              </button>
            ))}
          </div>
          {/* Year filter */}
          <div style={{ display: 'flex', gap: '4px' }}>
            {['all', ...years].map(y => (
              <button
                key={y}
                onClick={() => setYearFilter(y)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '8px',
                  border: '1px solid',
                  borderColor: yearFilter === y ? 'var(--border-default)' : 'var(--border-subtle)',
                  background: yearFilter === y ? 'var(--bg-elevated)' : 'transparent',
                  color: yearFilter === y ? 'var(--text-primary)' : 'var(--text-tertiary)',
                  fontSize: '12px',
                  fontFamily: 'var(--font-mono)',
                  cursor: 'pointer',
                }}
              >
                {y === 'all' ? 'Tudo' : y}
              </button>
            ))}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
          <div className="spinner" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
            {[
              { label: 'Gasto', value: formatBRL(totals.spend), color: 'var(--blue)' },
              { label: 'Impostos (ISS/PIS)', value: formatBRL(totals.tax), color: 'var(--accent)' },
              { label: 'Total Cobrado', value: formatBRL(totals.total), color: 'var(--red)' },
            ].map((card, i) => (
              <div
                key={card.label}
                className="fade-up"
                style={{
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border-subtle)',
                  borderLeft: `3px solid ${card.color}`,
                  borderRadius: '12px',
                  padding: '20px',
                  animationDelay: `${i * 0.06}s`,
                }}
              >
                <div style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                  {card.label}
                </div>
                <div style={{ fontSize: '28px', fontWeight: 600, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
                  {card.value}
                </div>
              </div>
            ))}
          </div>

          {/* Monthly Table */}
          <div style={{
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border-subtle)',
            borderRadius: '12px',
            overflow: 'hidden',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  {['Mês', 'Gasto', 'Impostos', 'Total'].map(h => (
                    <th key={h} style={{
                      padding: '12px 16px',
                      textAlign: h === 'Mês' ? 'left' : 'right',
                      fontSize: '11px',
                      fontWeight: 600,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase' as const,
                      color: 'var(--text-tertiary)',
                      fontFamily: 'var(--font-sans)',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredMonths.map((m, i) => (
                  <tr
                    key={m.monthStart}
                    style={{ borderBottom: i < filteredMonths.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-tertiary)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '13px', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                          {formatMonth(m.monthStart)}
                        </span>
                        <div style={{
                          height: '4px',
                          background: 'var(--bg-tertiary)',
                          borderRadius: '2px',
                          width: '80px',
                          overflow: 'hidden',
                        }}>
                          <div style={{
                            height: '100%',
                            background: 'var(--blue)',
                            borderRadius: '2px',
                            width: `${(m.spend / maxSpend) * 100}%`,
                          }} />
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '13px' }}>
                      {formatBRL(m.spend)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '13px', color: m.tax > 0 ? 'var(--accent)' : 'var(--text-tertiary)' }}>
                      {m.tax > 0 ? formatBRL(m.tax) : '—'}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '13px', fontWeight: 600 }}>
                      {formatBRL(m.total)}
                    </td>
                  </tr>
                ))}
                {/* Total row */}
                <tr style={{ borderTop: '2px solid var(--border-default)', background: 'var(--bg-tertiary)' }}>
                  <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}>Total</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '13px', fontWeight: 600 }}>{formatBRL(totals.spend)}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '13px', fontWeight: 600, color: 'var(--accent)' }}>{formatBRL(totals.tax)}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '13px', fontWeight: 700 }}>{formatBRL(totals.total)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Timestamp */}
          {data?.updatedAt && (
            <div style={{ marginTop: '12px', fontSize: '11px', color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)', textAlign: 'right' }}>
              Atualizado em {formatDatetime(data.updatedAt)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
